import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Check, X, Star, ShieldCheck, Clock, Map, Target, Calendar, Waves, MessageCircle, FileText, ListChecks, CalendarDays, Quote } from 'lucide-react';
// --- DATA ---
const GALLERY_IMAGES = ['https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1591291621164-2c6367723315?auto=format&fit=crop&q=80&w=800'];
const TOMOS = [{
  id: 1,
  title: 'TOMO 1 — El Sistema de Armado de Clase',
  icon: <Map className="w-5 h-5" data-id="element-0" />,
  quote: 'Nunca más arrancar de cero. Este es el molde que usás siempre.',
  items: ['La estructura tipo que funciona para cualquier nivel', 'Cómo armar una clase completa en 5 minutos', 'Qué va primero, qué va al medio, qué va al cierre', 'Errores comunes al estructurar una clase', 'Plantilla mental para replicar en cualquier situación'],
  image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80&w=800'
}, {
  id: 2,
  title: 'TOMO 2 — Secuencias por Duración',
  icon: <Clock className="w-5 h-5" data-id="element-1" />,
  quote: 'La clase de 30, de 45 y de 60 minutos, ya resueltas.',
  items: ['Secuencias completas de 30 minutos', 'Secuencias completas de 45 minutos', 'Secuencias completas de 60 y 90 minutos', 'Cómo estirar o acortar una secuencia sin que se rompa', 'Ritmo y tiempos de cada bloque'],
  image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800'
}, {
  id: 3,
  title: 'TOMO 3 — Secuencias por Objetivo',
  icon: <Target className="w-5 h-5" data-id="element-2" />,
  quote: 'Relajación, energía, dolor de espalda: una secuencia para cada necesidad.',
  items: ['Secuencia para relajación y cierre de día', 'Secuencia de energía matutina', 'Secuencia para dolor de espalda y cadera', 'Secuencia para principiantes absolutos', 'Secuencia para alumnos avanzados'],
  image: 'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?auto=format&fit=crop&q=80&w=800'
}, {
  id: 4,
  title: 'TOMO 4 — Secuencias por Contexto',
  icon: <Calendar className="w-5 h-5" data-id="element-3" />,
  quote: 'La clase de lunes cansado, la de pre-competencia, la de cierre de mes.',
  items: ['Clase para grupos con poca energía', 'Clase de activación pre-actividad física', 'Clase especial de cierre de mes o de ciclo', 'Clase para grupos mixtos de nivel', 'Cómo leer al grupo y elegir la secuencia correcta'],
  image: 'https://images.unsplash.com/photo-1603988363607-e1e4a66962c6?auto=format&fit=crop&q=80&w=800'
}, {
  id: 5,
  title: 'TOMO 5 — Transiciones y Fluidez',
  icon: <Waves className="w-5 h-5" data-id="element-4" />,
  quote: 'Lo que hace que una clase se sienta armada, no pegoteada.',
  items: ['Puentes entre posturas para que no se corte el ritmo', 'Cómo pasar de pie a suelo sin quiebres', 'Respiración como guía de transición', 'Secuencias de transición para cada bloque', 'Errores de fluidez más comunes y cómo corregirlos'],
  image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800'
}, {
  id: 6,
  title: 'TOMO 6 — Guion de Voz y Cierre',
  icon: <MessageCircle className="w-5 h-5" data-id="element-5" />,
  quote: 'Qué decir mientras enseñás, para que no te quedes en blanco al hablar.',
  items: ['Guiones de apertura de clase', 'Cues verbales para cada postura clave', 'Cómo guiar la respiración con la voz', 'Guion de cierre y meditación final', 'Frases de motivación sin sonar forzado'],
  image: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&q=80&w=800'
}];
const BONUSES = [{
  id: 1,
  title: 'BONO 01 — Planilla de Secuencia Imprimible',
  icon: <FileText className="w-5 h-5" data-id="element-6" />,
  value: '$8.000',
  quote: 'Llevá la secuencia impresa al salón, sin depender del celular.',
  desc: 'Para qué sirve: Para tener la clase a mano en formato físico, sin pantallas de por medio, lista para consultar de un vistazo entre postura y postura.',
  image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&q=80&w=800'
}, {
  id: 2,
  title: 'BONO 02 — Checklist Pre-Clase de 2 Minutos',
  icon: <ListChecks className="w-5 h-5" data-id="element-7" />,
  value: '$7.000',
  quote: 'El repaso rápido que hacés antes de que entren los alumnos.',
  desc: 'Para qué sirve: Para llegar segura al salón, sabiendo que no te olvidaste nada — desde el mat hasta el orden de la secuencia.',
  image: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&q=80&w=800'
}, {
  id: 3,
  title: 'BONO 03 — Calendario Mensual de Clases',
  icon: <CalendarDays className="w-5 h-5" data-id="element-8" />,
  value: '$9.000',
  quote: 'Un mes entero de clases ya distribuidas y variadas.',
  desc: 'Para qué sirve: Para que tus alumnos noten variedad real semana a semana, sin que vos tengas que planificar desde cero cada vez.',
  image: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&q=80&w=800'
}, {
  id: 4,
  title: 'BONO 04 — Banco de Frases de Apertura y Cierre',
  icon: <Quote className="w-5 h-5" data-id="element-9" />,
  value: '$8.000',
  quote: 'Frases listas para arrancar y cerrar cualquier clase.',
  desc: 'Para qué sirve: Para que la bienvenida y la despedida se sientan cuidadas, sin tener que improvisar las palabras justo cuando más nervios da.',
  image: 'https://images.unsplash.com/photo-1600881333168-2ef49b341f30?auto=format&fit=crop&q=80&w=800'
}];
const FAQS = [{
  q: '¿Sirve si recién me certifiqué?',
  a: 'Sí. Las secuencias están armadas para usar tal cual, sin necesidad de años de experiencia dando clases.'
}, {
  q: '¿Tengo que adaptar las secuencias o ya están listas?',
  a: 'Ya están listas para ejecutar. También incluyen variantes por si querés adaptarlas a tu grupo.'
}, {
  q: '¿Cómo recibo el material?',
  a: 'Acceso inmediato después del pago. Digital — celular, tablet o computadora. También podés imprimirlo.'
}, {
  q: '¿Qué diferencia hay entre los dos planes?',
  a: 'El Sistema Base tiene los 6 tomos y 4 bonos — ideal para nunca más improvisar una clase. El Plan PRO agrega 3 herramientas de gestión para profesionalizar tu trabajo con alumnos.'
}, {
  q: '¿Sirve para cualquier estilo de yoga o solo Hatha?',
  a: 'Las secuencias están pensadas en base a Hatha Yoga, pero la estructura y el sistema de armado se puede aplicar a cualquier estilo.'
}, {
  q: '¿Tiene garantía real?',
  a: 'Sí. 7 días, sin preguntas. Te devolvemos el 100%. Menos del 1% pide devolución.'
}];
// --- COMPONENTS ---
const UrgencyBar = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 59,
    s: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.s > 0) return {
          ...prev,
          s: prev.s - 1
        };
        if (prev.m > 0) return {
          ...prev,
          m: prev.m - 1,
          s: 59
        };
        if (prev.h > 0) return {
          h: prev.h - 1,
          m: 59,
          s: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="sticky top-0 z-50 bg-ciruela text-white text-[11px] font-medium py-1 px-4 text-center shadow-md" data-id="element-10">
      ⏰ Precio de lanzamiento — cierra en:{' '}
      {String(timeLeft.h).padStart(2, '0')}:
      {String(timeLeft.m).padStart(2, '0')}:
      {String(timeLeft.s).padStart(2, '0')}
    </div>;
};
const AccordionItem = ({
  title,
  icon,
  children,
  isOpen,
  onClick,
  isDark = false
}: any) => {
  return <div className={`border-b ${isDark ? 'border-white/20' : 'border-verde-oscuro/10'} last:border-0`} data-id="element-11">
      <button onClick={onClick} className="w-full py-4 px-2 flex items-center justify-between text-left focus:outline-none" data-id="element-12">
        <div className="flex items-center gap-3 font-semibold pr-4" data-id="element-13">
          {icon && <span className={isDark ? 'text-crema' : 'text-ciruela'} data-id="element-14">
              {icon}
            </span>}
          <span data-id="element-15">{title}</span>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0" data-id="element-16">
          <ChevronDown className="w-5 h-5" data-id="element-17" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-18">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-19">
            <div className="pb-5 px-2" data-id="element-20">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function App() {
  const [currentGalleryIdx, setCurrentGalleryIdx] = useState(0);
  const [openTomo, setOpenTomo] = useState<number | null>(1);
  const [openBono, setOpenBono] = useState<number | null>(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'base' | 'pro'>('pro');
  const nextGalleryImage = () => setCurrentGalleryIdx(p => (p + 1) % GALLERY_IMAGES.length);
  const prevGalleryImage = () => setCurrentGalleryIdx(p => (p - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 59,
    s: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.s > 0) return {
          ...prev,
          s: prev.s - 1
        };
        if (prev.m > 0) return {
          ...prev,
          m: prev.m - 1,
          s: 59
        };
        if (prev.h > 0) return {
          h: prev.h - 1,
          m: 59,
          s: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-crema text-verde-oscuro font-sans selection:bg-ciruela selection:text-white" data-id="element-21">
      <UrgencyBar data-id="element-22" />

      {/* HERO SECTION */}
      <section className="relative flex flex-col justify-start items-center px-5 pt-4 pb-8 text-center max-w-md mx-auto" data-id="element-23">
        <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-verde-oscuro/10 px-3 py-1 rounded-full text-xs font-medium mb-3" data-id="element-24">
          <span data-id="element-25">🧘‍♀️</span> Para Profesoras de Yoga · Edición 2026
        </div>

        <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-2" data-id="element-26">
          Tus clases de Yoga <span className="text-ciruela" data-id="element-27">Resueltas</span>.
        </h1>

        <motion.div animate={{
        y: [0, -10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 4,
        ease: 'easeInOut'
      }} className="w-full max-w-[400px] mx-auto -my-2 relative z-10" data-id="element-28">
          <img src="https://cdn.magicpatterns.com/uploads/iu1zGLE8mcrQbsmN29AVH7/Untitled_design_(3).png" alt="Yoga Clase Resuelta PRO Mockup" className="w-full h-auto drop-shadow-2xl" data-id="element-29" />
        </motion.div>

        <p className="text-sm text-texto-sec mb-3 px-2" data-id="element-30">
          El sistema de secuencias listas para que nunca más llegues a dar clase
          sin saber por dónde arrancar.
        </p>

        <div className="w-full space-y-2.5 mb-3" data-id="element-31">
          <button onClick={scrollToPricing} className="w-full bg-ciruela text-white h-12 rounded-xl font-bold text-base shadow-lg shadow-ciruela/20 active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-32">
            <span data-id="element-33">🧘‍♀️</span> Quiero Yoga Clase Resuelta PRO
          </button>
          <button onClick={() => document.getElementById('tomos')?.scrollIntoView({
          behavior: 'smooth'
        })} className="w-full border-2 border-verde-oscuro text-verde-oscuro h-12 rounded-xl font-bold text-base active:scale-[0.98] transition-transform" data-id="element-34">
            Ver qué incluye ↓
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs font-medium text-texto-sec" data-id="element-35">
          <span className="flex items-center gap-1" data-id="element-36">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-37" /> 4.9/5
          </span>
          <span data-id="element-38">·</span>
          <span data-id="element-39">+900 profesoras</span>
          <span data-id="element-40">·</span>
          <span data-id="element-41">📦 +80 secuencias</span>
          <span data-id="element-42">·</span>
          <span className="flex items-center gap-1" data-id="element-43">
            <ShieldCheck className="w-4 h-4" data-id="element-44" /> Garantía 7 días
          </span>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-white py-12" data-id="element-45">
        <div className="max-w-md mx-auto px-5" data-id="element-46">
          <h2 className="text-center font-display text-xl font-bold mb-6" data-id="element-47">
            Así se sienten las clases cuando ya no improvisás
          </h2>

          <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 shadow-sm" data-id="element-48">
            <AnimatePresence mode="wait" data-id="element-49">
              <motion.img key={currentGalleryIdx} src={GALLERY_IMAGES[currentGalleryIdx]} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="w-full h-full object-cover absolute inset-0" data-id="element-50" />
            </AnimatePresence>

            <button onClick={prevGalleryImage} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md text-verde-oscuro z-10" data-id="element-51">
              <ChevronLeft className="w-6 h-6" data-id="element-52" />
            </button>
            <button onClick={nextGalleryImage} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md text-verde-oscuro z-10" data-id="element-53">
              <ChevronRight className="w-6 h-6" data-id="element-54" />
            </button>

            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10" data-id="element-55">
              {GALLERY_IMAGES.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === currentGalleryIdx ? 'bg-white' : 'bg-white/50'}`} data-id="element-56" />)}
            </div>
          </div>
          <p className="text-center text-sm text-texto-sec mt-4 italic" data-id="element-57">
            {['Secuencia de apertura, lista en segundos', 'Transiciones que fluyen, sin pensarlo', 'Cierre de clase, guionado y calmo', 'Variantes para cada nivel', 'Confianza al guiar'][currentGalleryIdx]}
          </p>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-12 max-w-md mx-auto px-5" data-id="element-58">
        <h2 className="font-display text-2xl font-bold text-center mb-8" data-id="element-59">
          Lo que dicen las profes que ya lo usan
        </h2>
        <div className="space-y-4" data-id="element-60">
          {[{
          t: 'Antes armaba la clase la noche anterior, a las apuradas. Ahora abro el tomo que necesito y ya está resuelto.',
          a: 'Florencia R. · Profesora de Hatha Yoga, CABA'
        }, {
          t: 'Lo que más cambió fue la ansiedad antes de entrar al salón. Ya sé exactamente qué voy a dar.',
          a: 'Valentina S. · Instructora certificada, Córdoba'
        }, {
          t: 'Las secuencias por objetivo me salvaron con un alumno con dolor de espalda. Tenía la variante lista al toque.',
          a: 'Micaela D. · Yoga terapéutico, Rosario'
        }].map((r, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-verde-oscuro/5" data-id="element-61">
              <div className="flex gap-1 mb-3" data-id="element-62">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-63" />)}
              </div>
              <p className="font-medium text-lg mb-4 leading-snug" data-id="element-64">"{r.t}"</p>
              <p className="text-sm text-texto-sec" data-id="element-65">— {r.a}</p>
            </div>)}
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="bg-verde-oscuro text-white py-16" data-id="element-66">
        <div className="max-w-md mx-auto px-5" data-id="element-67">
          <h2 className="font-display text-3xl font-bold text-center mb-10" data-id="element-68">
            ¿Este sistema es para vos?
          </h2>
          <div className="space-y-6" data-id="element-69">
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/10" data-id="element-70">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" data-id="element-71">
                <span data-id="element-72">🧘‍♀️</span> Profesoras recién recibidas
              </h3>
              <p className="text-crema/80 mb-4 text-sm leading-relaxed" data-id="element-73">
                Te certificaste pero nadie te enseñó a armar una clase de
                verdad. Con Yoga Clase Resuelta PRO tenés secuencias completas,
                listas para dar desde el primer día.
              </p>
              <ul className="space-y-2 text-sm font-medium" data-id="element-74">
                <li className="flex items-center gap-2" data-id="element-75">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-76" /> Estructura de clase
                  paso a paso
                </li>
                <li className="flex items-center gap-2" data-id="element-77">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-78" /> Guion de qué decir
                  en cada momento
                </li>
              </ul>
            </div>

            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/10" data-id="element-79">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" data-id="element-80">
                <span data-id="element-81">📅</span> Profesoras con alumnos fijos
              </h3>
              <p className="text-crema/80 mb-4 text-sm leading-relaxed" data-id="element-82">
                Repetís las mismas posturas porque no tenés tiempo de investigar
                variantes. Con el banco de secuencias por objetivo, cada clase
                se siente distinta y pensada.
              </p>
              <ul className="space-y-2 text-sm font-medium" data-id="element-83">
                <li className="flex items-center gap-2" data-id="element-84">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-85" /> Secuencias por
                  duración y objetivo
                </li>
                <li className="flex items-center gap-2" data-id="element-86">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-87" /> Variantes para
                  adaptar en el momento
                </li>
              </ul>
            </div>

            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/10" data-id="element-88">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" data-id="element-89">
                <span data-id="element-90">💼</span> Instructoras que quieren profesionalizarse
              </h3>
              <p className="text-crema/80 mb-4 text-sm leading-relaxed" data-id="element-91">
                Querés ofrecer más que "una clase más de yoga". El Plan PRO te
                da las herramientas de gestión para que tu trabajo se vea — y se
                sienta — profesional.
              </p>
              <ul className="space-y-2 text-sm font-medium" data-id="element-92">
                <li className="flex items-center gap-2" data-id="element-93">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-94" /> Registro de
                  progreso de alumnos
                </li>
                <li className="flex items-center gap-2" data-id="element-95">
                  <Check className="w-4 h-4 text-ciruela" data-id="element-96" /> Plantillas de
                  feedback y asistencia
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-white py-16" data-id="element-97">
        <div className="max-w-md mx-auto px-5" data-id="element-98">
          <h2 className="font-display text-3xl font-bold mb-4" data-id="element-99">
            ¿Cuántas clases más vas a improvisar?
          </h2>
          <p className="text-lg text-texto-sec mb-8" data-id="element-100">
            Sin un sistema claro, siempre termina igual.
          </p>

          <ul className="space-y-4 mb-10" data-id="element-101">
            {['Llegás al salón y la mente se queda en blanco', 'Armás la secuencia la noche anterior, a las apuradas', 'Repetís las mismas posturas porque no tenés tiempo de variar', 'Un alumno pide algo puntual y no tenés nada preparado', 'La clase se siente desordenada y vos lo notás primero que nadie', 'Empezás con ganas, te agotás organizando, y la próxima clase es lo mismo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-verde-oscuro" data-id="element-102">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-103" />
                <span className="leading-snug" data-id="element-104">{item}</span>
              </li>)}
          </ul>

          <div className="bg-ciruela text-white p-6 rounded-2xl shadow-lg" data-id="element-105">
            <p className="font-bold text-lg mb-2" data-id="element-106">
              No es falta de conocimiento. Es falta de un sistema.
            </p>
            <p className="text-white/90" data-id="element-107">
              Con Yoga Clase Resuelta PRO, tu próxima clase ya está armada. 🧘‍♀️
            </p>
          </div>
        </div>
      </section>

      {/* THE 6 TOMES */}
      <section id="tomos" className="py-16 max-w-md mx-auto px-5" data-id="element-108">
        <div className="inline-block bg-oliva/10 text-oliva px-3 py-1 rounded-full text-sm font-bold mb-4" data-id="element-109">
          📦 Sistema Completo
        </div>
        <h2 className="font-display text-3xl font-bold mb-3" data-id="element-110">
          6 tomos. +80 secuencias. Todo resuelto.
        </h2>
        <p className="text-lg text-texto-sec mb-8" data-id="element-111">
          De la clase en blanco a la clase que fluye sola.
        </p>

        <div className="bg-white rounded-2xl shadow-sm border border-verde-oscuro/10 overflow-hidden" data-id="element-112">
          {TOMOS.map(tomo => <AccordionItem key={tomo.id} title={tomo.title} icon={tomo.icon} isOpen={openTomo === tomo.id} onClick={() => setOpenTomo(openTomo === tomo.id ? null : tomo.id)} data-id="element-113">
              <div className="space-y-4 pt-2" data-id="element-114">
                <p className="italic text-texto-sec border-l-2 border-ciruela pl-3 py-1" data-id="element-115">
                  "{tomo.quote}"
                </p>
                <ul className="space-y-2" data-id="element-116">
                  {tomo.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-117">
                      <Check className="w-4 h-4 text-oliva flex-shrink-0 mt-0.5" data-id="element-118" />
                      <span data-id="element-119">{item}</span>
                    </li>)}
                </ul>
                <img src={tomo.image} alt={tomo.title} className="w-full h-40 object-cover rounded-xl mt-4" data-id="element-120" />
              </div>
            </AccordionItem>)}
        </div>

        <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm border border-verde-oscuro/10" data-id="element-121">
          <h3 className="font-bold mb-4" data-id="element-122">Cada tomo incluye:</h3>
          <ul className="space-y-2 text-sm" data-id="element-123">
            {['Secuencia lista para ejecutar tal cual', 'Guion de qué decir en cada momento', 'Tiempos sugeridos por bloque', 'Variantes según nivel del grupo', '100% adaptado a clases reales en Argentina'].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-124">
                <Check className="w-4 h-4 text-ciruela" data-id="element-125" /> {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* BONUSES */}
      <section className="bg-ciruela text-white py-16" data-id="element-126">
        <div className="max-w-md mx-auto px-5" data-id="element-127">
          <h2 className="font-display text-3xl font-bold mb-3" data-id="element-128">
            4 bonos incluidos. Gratis. Siempre.
          </h2>
          <p className="text-lg text-white/80 mb-8" data-id="element-129">
            Valor total: $32.000 ARS → $0 con tu sistema.
          </p>

          <div className="bg-white/10 rounded-2xl border border-white/20 overflow-hidden backdrop-blur-sm" data-id="element-130">
            {BONUSES.map(bono => <AccordionItem key={bono.id} title={bono.title} icon={bono.icon} isOpen={openBono === bono.id} onClick={() => setOpenBono(openBono === bono.id ? null : bono.id)} isDark={true} data-id="element-131">
                <div className="space-y-4 pt-2" data-id="element-132">
                  <div className="flex items-center gap-2 font-bold" data-id="element-133">
                    <span className="line-through text-white/50" data-id="element-134">
                      {bono.value}
                    </span>
                    <span className="text-crema bg-white/20 px-2 py-0.5 rounded text-sm" data-id="element-135">
                      GRATIS
                    </span>
                  </div>
                  <p className="italic text-white/90 border-l-2 border-white/30 pl-3 py-1" data-id="element-136">
                    "{bono.quote}"
                  </p>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-137">
                    {bono.desc}
                  </p>
                  <img src={bono.image} alt={bono.title} className="w-full h-40 object-cover rounded-xl mt-4 opacity-90" data-id="element-138" />
                </div>
              </AccordionItem>)}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white py-12" data-id="element-139">
        <div className="max-w-md mx-auto px-5" data-id="element-140">
          <h2 className="font-display text-xl font-bold text-center mb-8" data-id="element-141">
            Sin Yoga Clase Resuelta PRO vs. Con Yoga Clase Resuelta PRO
          </h2>

          <div className="space-y-3" data-id="element-142">
            {[{
            bad: 'Armás cada clase desde cero',
            good: 'Secuencias listas por duración y objetivo'
          }, {
            bad: 'Te quedás en blanco al arrancar',
            good: 'Guion de voz para cada momento'
          }, {
            bad: 'Repetís siempre las mismas posturas',
            good: 'Banco de +80 secuencias variadas'
          }, {
            bad: 'No tenés nada para alumnos puntuales',
            good: 'Variantes por nivel y necesidad'
          }, {
            bad: 'La clase se siente desordenada',
            good: 'Transiciones guionadas, todo fluye'
          }].map((row, i) => <div key={i} className="flex flex-col gap-2 p-4 rounded-xl bg-crema/50 border border-verde-oscuro/5" data-id="element-143">
                <div className="flex items-start gap-2 text-sm text-texto-sec" data-id="element-144">
                  <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-145" />
                  <span data-id="element-146">{row.bad}</span>
                </div>
                <div className="flex items-start gap-2 text-sm font-medium text-verde-oscuro" data-id="element-147">
                  <Check className="w-4 h-4 text-oliva mt-0.5 flex-shrink-0" data-id="element-148" />
                  <span data-id="element-149">{row.good}</span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-verde-oscuro text-white py-16" data-id="element-150">
        <div className="max-w-md mx-auto px-5" data-id="element-151">
          {/* TIMER */}
          <div className="text-center mb-10" data-id="element-152">
            <p className="text-sm font-bold tracking-widest text-ciruela mb-3 uppercase" data-id="element-153">
              Esta oferta termina en
            </p>
            <div className="flex justify-center gap-3" data-id="element-154">
              <div className="flex flex-col items-center" data-id="element-155">
                <div className="bg-verde-timer w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-bold font-display shadow-inner border border-white/5" data-id="element-156">
                  {String(timeLeft.h).padStart(2, '0')}
                </div>
                <span className="text-[10px] mt-2 text-white/50 font-medium" data-id="element-157">
                  HORAS
                </span>
              </div>
              <span className="text-3xl font-bold mt-3 text-white/20" data-id="element-158">:</span>
              <div className="flex flex-col items-center" data-id="element-159">
                <div className="bg-verde-timer w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-bold font-display shadow-inner border border-white/5" data-id="element-160">
                  {String(timeLeft.m).padStart(2, '0')}
                </div>
                <span className="text-[10px] mt-2 text-white/50 font-medium" data-id="element-161">
                  MIN
                </span>
              </div>
              <span className="text-3xl font-bold mt-3 text-white/20" data-id="element-162">:</span>
              <div className="flex flex-col items-center" data-id="element-163">
                <div className="bg-verde-timer w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-bold font-display shadow-inner border border-white/5" data-id="element-164">
                  {String(timeLeft.s).padStart(2, '0')}
                </div>
                <span className="text-[10px] mt-2 text-white/50 font-medium" data-id="element-165">
                  SEG
                </span>
              </div>
            </div>
          </div>

          <div className="text-center mb-8" data-id="element-166">
            <h2 className="font-display text-3xl font-bold mb-2" data-id="element-167">
              ELEGÍ TU
              <br data-id="element-168" />
              YOGA CLASE RESUELTA PRO
            </h2>
            <p className="text-ciruela text-sm font-medium" data-id="element-169">
              Ambas opciones incluyen los 6 tomos y los 4 bonos base
            </p>
          </div>

          <div className="space-y-4 mb-8" data-id="element-170">
            {/* PLAN BASE */}
            <label className={`block relative p-5 rounded-2xl border-2 cursor-pointer transition-all ${selectedPlan === 'base' ? 'border-ciruela bg-white/10' : 'border-white/10 bg-white/5'}`} onClick={() => setSelectedPlan('base')} data-id="element-171">
              <div className="flex items-start gap-3" data-id="element-172">
                <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center ${selectedPlan === 'base' ? 'border-ciruela' : 'border-white/30'}`} data-id="element-173">
                  {selectedPlan === 'base' && <div className="w-3 h-3 rounded-full bg-ciruela" data-id="element-174" />}
                </div>
                <div className="flex-1" data-id="element-175">
                  <h3 className="font-bold text-lg flex items-center gap-2" data-id="element-176">
                    <span data-id="element-177">🧘‍♀️</span> SISTEMA BASE
                  </h3>
                  <p className="text-sm text-white/70 mt-1 mb-3" data-id="element-178">
                    6 tomos + 4 bonos estratégicos
                  </p>
                  <div className="flex items-end gap-2" data-id="element-179">
                    <span className="line-through text-white/40 text-sm mb-1" data-id="element-180">
                      $34.990
                    </span>
                    <span className="font-display text-2xl font-bold" data-id="element-181">
                      $18.990
                    </span>
                  </div>
                </div>
              </div>
            </label>

            {/* PLAN PRO */}
            <label className={`block relative p-5 rounded-2xl border-2 cursor-pointer transition-all ${selectedPlan === 'pro' ? 'border-ciruela bg-white/10' : 'border-white/10 bg-white/5'}`} onClick={() => setSelectedPlan('pro')} data-id="element-182">
              <div className="absolute -top-3 right-4 bg-ciruela text-white text-[10px] font-bold px-2 py-1 rounded-full tracking-wide" data-id="element-183">
                ⭐ MÁS ELEGIDO
              </div>
              <div className="flex items-start gap-3" data-id="element-184">
                <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center ${selectedPlan === 'pro' ? 'border-ciruela' : 'border-white/30'}`} data-id="element-185">
                  {selectedPlan === 'pro' && <div className="w-3 h-3 rounded-full bg-ciruela" data-id="element-186" />}
                </div>
                <div className="flex-1" data-id="element-187">
                  <h3 className="font-bold text-lg flex items-center gap-2" data-id="element-188">
                    <span data-id="element-189">🚀</span> PLAN PRO
                  </h3>
                  <p className="text-sm text-white/70 mt-1 mb-3" data-id="element-190">
                    6 tomos + 4 bonos + 3 herramientas de gestión PRO
                  </p>
                  <div className="flex items-center justify-between" data-id="element-191">
                    <div className="flex items-end gap-2" data-id="element-192">
                      <span className="line-through text-white/40 text-sm mb-1" data-id="element-193">
                        $54.990
                      </span>
                      <span className="font-display text-2xl font-bold" data-id="element-194">
                        $27.990
                      </span>
                    </div>
                    <span className="bg-oliva text-white text-[10px] font-bold px-2 py-1 rounded" data-id="element-195">
                      AHORRÁS $27.000
                    </span>
                  </div>
                </div>
              </div>
            </label>
          </div>

          <a href={selectedPlan === 'pro' ? 'https://checkout.tusbeneficios.shop/cart/51495402307817:1' : 'https://checkout.tusbeneficios.shop/cart/51495401259241:1'} className="w-full bg-ciruela text-white h-14 rounded-xl font-bold text-lg shadow-lg shadow-ciruela/20 active:scale-[0.98] transition-transform mb-3 flex items-center justify-center" data-id="element-196">
            SÍ, QUIERO EL {selectedPlan === 'pro' ? 'PLAN PRO' : 'SISTEMA BASE'}{' '}
            →
          </a>
          <p className="text-center text-xs text-white/60 mb-8" data-id="element-197">
            La opción más elegida por profesoras que quieren dar clases sin
            improvisar
          </p>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs font-medium text-white/80" data-id="element-198">
            <span className="flex items-center gap-1" data-id="element-199">
              <ShieldCheck className="w-4 h-4" data-id="element-200" /> Pago seguro
            </span>
            <span data-id="element-201">·</span>
            <span className="flex items-center gap-1" data-id="element-202">
              <Clock className="w-4 h-4" data-id="element-203" /> Acceso inmediato
            </span>
            <span data-id="element-204">·</span>
            <span className="flex items-center gap-1" data-id="element-205">
              <ShieldCheck className="w-4 h-4" data-id="element-206" /> Garantía 7 días
            </span>
          </div>
        </div>
      </section>

      {/* 3 PRO TOOLS */}
      <section className="py-16 max-w-md mx-auto px-5" data-id="element-207">
        <div className="text-center mb-10" data-id="element-208">
          <h2 className="font-display text-3xl font-bold mb-3" data-id="element-209">
            Qué vas a obtener con el PLAN PRO
          </h2>
          <p className="text-texto-sec" data-id="element-210">
            La diferencia entre tener secuencias sueltas y un sistema completo
            de gestión de clases
          </p>
        </div>

        <div className="space-y-4 mb-10" data-id="element-211">
          <div className="bg-white p-5 rounded-2xl border border-verde-oscuro/10" data-id="element-212">
            <div className="text-sm font-bold text-red-500 mb-2 flex items-center gap-1" data-id="element-213">
              <X className="w-4 h-4" data-id="element-214" /> SI NO TENÉS EL PLAN PRO
            </div>
            <p className="font-bold mb-3" data-id="element-215">Solo tenés las secuencias en PDF</p>
            <ul className="space-y-2 text-sm text-texto-sec" data-id="element-216">
              <li className="flex items-start gap-2" data-id="element-217">
                <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-218" />{' '}
                Secuencias básicas, sin herramientas de gestión
              </li>
              <li className="flex items-start gap-2" data-id="element-219">
                <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-220" />{' '}
                Armás el seguimiento de alumnos desde cero
              </li>
              <li className="flex items-start gap-2" data-id="element-221">
                <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-222" /> Sin
                plantillas para cobrar o profesionalizar tu trabajo
              </li>
              <li className="flex items-start gap-2" data-id="element-223">
                <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-224" /> Sin
                actualizaciones futuras
              </li>
            </ul>
          </div>

          <div className="bg-verde-oscuro text-white p-5 rounded-2xl border-2 border-ciruela relative shadow-lg" data-id="element-225">
            <div className="absolute -top-3 right-4 bg-ciruela text-white text-[10px] font-bold px-2 py-1 rounded-full" data-id="element-226">
              RECOMENDADO
            </div>
            <div className="text-sm font-bold text-oliva mb-2 flex items-center gap-1" data-id="element-227">
              <Check className="w-4 h-4" data-id="element-228" /> SI TENÉS EL PLAN PRO
            </div>
            <p className="font-bold mb-3" data-id="element-229">
              Tenés un sistema completo de gestión de clases
            </p>
            <ul className="space-y-2 text-sm text-white/80" data-id="element-230">
              <li className="flex items-start gap-2" data-id="element-231">
                <Check className="w-4 h-4 text-oliva flex-shrink-0 mt-0.5" data-id="element-232" />{' '}
                Herramientas premium de seguimiento y registro
              </li>
              <li className="flex items-start gap-2" data-id="element-233">
                <Check className="w-4 h-4 text-oliva flex-shrink-0 mt-0.5" data-id="element-234" />{' '}
                Plantillas de gestión pre-armadas
              </li>
              <li className="flex items-start gap-2" data-id="element-235">
                <Check className="w-4 h-4 text-oliva flex-shrink-0 mt-0.5" data-id="element-236" />{' '}
                Recursos para profesionalizar y cobrar tu trabajo
              </li>
              <li className="flex items-start gap-2" data-id="element-237">
                <Check className="w-4 h-4 text-oliva flex-shrink-0 mt-0.5" data-id="element-238" />{' '}
                Acceso a futuras actualizaciones
              </li>
            </ul>
          </div>
        </div>

        <div className="space-y-4" data-id="element-239">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-verde-oscuro/5 flex gap-4 items-start" data-id="element-240">
            <div className="w-12 h-12 rounded-full bg-crema flex items-center justify-center flex-shrink-0 text-2xl" data-id="element-241">
              📊
            </div>
            <div data-id="element-242">
              <h3 className="font-bold mb-2 leading-tight" data-id="element-243">
                PLAN PRO #1 — Registro de Progreso de Alumnos
              </h3>
              <p className="text-sm text-texto-sec leading-relaxed" data-id="element-244">
                Planilla lista para llevar el seguimiento de cada alumno:
                asistencia, evolución y objetivos. Ahorrá horas de armado
                copiando una estructura ya validada.
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-verde-oscuro/5 flex gap-4 items-start" data-id="element-245">
            <div className="w-12 h-12 rounded-full bg-crema flex items-center justify-center flex-shrink-0 text-2xl" data-id="element-246">
              📝
            </div>
            <div data-id="element-247">
              <h3 className="font-bold mb-2 leading-tight" data-id="element-248">
                PLAN PRO #2 — Plantillas de Feedback y Autoevaluación
              </h3>
              <p className="text-sm text-texto-sec leading-relaxed" data-id="element-249">
                Formularios listos para pedir feedback a tus alumnos y evaluar
                tu propia clase después de cada sesión — para mejorar sin
                adivinar qué falló.
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-verde-oscuro/5 flex gap-4 items-start" data-id="element-250">
            <div className="w-12 h-12 rounded-full bg-crema flex items-center justify-center flex-shrink-0 text-2xl" data-id="element-251">
              ♾️
            </div>
            <div data-id="element-252">
              <h3 className="font-bold mb-2 leading-tight" data-id="element-253">
                PLAN PRO #3 — Actualizaciones de por Vida
              </h3>
              <p className="text-sm text-texto-sec leading-relaxed" data-id="element-254">
                Acceso gratuito a cada nueva secuencia, guion o formato que
                sumemos al sistema en el futuro.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="bg-white py-16" data-id="element-255">
        <div className="max-w-md mx-auto px-5 text-center" data-id="element-256">
          <div className="w-16 h-16 bg-crema rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-257">
            <ShieldCheck className="w-8 h-8 text-verde-oscuro" data-id="element-258" />
          </div>
          <h2 className="font-display text-2xl font-bold mb-4" data-id="element-259">
            Garantía 7 días — Sin preguntas
          </h2>
          <p className="text-texto-sec mb-6" data-id="element-260">
            Abrís el sistema. Lo usás en tu próxima clase. Si en 7 días no es lo
            que esperabas, te devolvemos el 100% sin formularios, sin drama.
          </p>
          <div className="inline-block bg-oliva/10 text-oliva px-3 py-1 rounded-full text-sm font-bold" data-id="element-261">
            Tasa de devoluciones: menos del 1%.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 max-w-md mx-auto px-5" data-id="element-262">
        <h2 className="font-display text-3xl font-bold text-center mb-8" data-id="element-263">
          Lo que más nos preguntan
        </h2>
        <div className="bg-white rounded-2xl shadow-sm border border-verde-oscuro/10 overflow-hidden" data-id="element-264">
          {FAQS.map((faq, i) => <AccordionItem key={i} title={faq.q} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-265">
              <p className="text-sm text-texto-sec leading-relaxed" data-id="element-266">{faq.a}</p>
            </AccordionItem>)}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-verde-oscuro text-white py-16 text-center" data-id="element-267">
        <div className="max-w-md mx-auto px-5" data-id="element-268">
          <h2 className="font-display text-3xl font-bold mb-8" data-id="element-269">
            Tu próxima clase ya puede estar resuelta. 🧘‍♀️
          </h2>

          <div className="space-y-4 mb-8" data-id="element-270">
            <button onClick={scrollToPricing} className="w-full bg-ciruela text-white h-14 rounded-xl font-bold text-lg shadow-lg shadow-ciruela/20 active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-271">
              <span data-id="element-272">🧘‍♀️</span> Quiero Yoga Clase Resuelta PRO
            </button>
            <button onClick={scrollToPricing} className="w-full border-2 border-white/20 text-white h-14 rounded-xl font-bold text-lg active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-273">
              <span data-id="element-274">🚀</span> Quiero el Plan PRO
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs font-medium text-white/60" data-id="element-275">
            <span className="flex items-center gap-1" data-id="element-276">
              <ShieldCheck className="w-4 h-4" data-id="element-277" /> Garantía 7 días
            </span>
            <span data-id="element-278">·</span>
            <span className="flex items-center gap-1" data-id="element-279">
              <ShieldCheck className="w-4 h-4" data-id="element-280" /> Pago seguro
            </span>
            <span data-id="element-281">·</span>
            <span className="flex items-center gap-1" data-id="element-282">
              <Clock className="w-4 h-4" data-id="element-283" /> Acceso inmediato
            </span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-verde-oscuro border-t border-white/10 text-white py-12 text-center" data-id="element-284">
        <div className="max-w-md mx-auto px-5" data-id="element-285">
          <div className="font-display font-bold text-xl mb-4" data-id="element-286">
            🧘‍♀️ Yoga Clase Resuelta™ PRO
          </div>
          <p className="text-sm text-white/70 mb-8" data-id="element-287">
            El sistema N°1 para profesoras de yoga que quieren dar clases
            organizadas, sin improvisar y con confianza real.
          </p>
          <div className="flex justify-center gap-4 text-sm text-white/50 mb-8" data-id="element-288">
            <a href="#" className="hover:text-white transition-colors" data-id="element-289">
              Email
            </a>
            <span data-id="element-290">·</span>
            <a href="#" className="hover:text-white transition-colors" data-id="element-291">
              Instagram
            </a>
            <span data-id="element-292">·</span>
            <a href="#" className="hover:text-white transition-colors" data-id="element-293">
              Sitio web
            </a>
          </div>
          <p className="text-xs text-white/40 mb-4" data-id="element-294">
            © Yoga Clase Resuelta™ PRO 2026 — Todos los derechos reservados.
          </p>
          <p className="text-sm italic text-white/60" data-id="element-295">
            🧘‍♀️ "Menos estrés, más impacto en tus clases."
          </p>
        </div>
      </footer>
    </div>;
}